import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { TooltipProvider, TooltipRoot, TooltipTrigger } from '@/components/ui/tooltip';
import { FC, useState, useMemo, memo } from 'react';
import { Button } from '@/components/ui/button';
import { trpc } from 'src/trpc';
import { OrderStatus, OrderStatusEnum, AdditionalLineItem } from '@libs/schemas/src/order';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DiscountDialog } from 'components/orders/Details/DiscountDialog';
import { DiscountType, safeMoneyFromCents } from '@libs/services/money';
import { AdditionalLineItemDialog } from 'components/orders/Details/AdditionalLineItemDialog';
import { CreatePaymentDrawer } from 'components/orders/Details/CreatePaymentDrawer';

import { PickupRequestModal } from 'components/orders/Details/PickupRequestModal';
import { ManualStatusChangeDialog } from 'components/orders/Details/ManualStatusChangeDialog';
import { OrderAddOnsDialog } from 'components/orders/Details/OrderAddOnsDialog';
import { OrderAddOn } from '@libs/models';
import type { Cart, Order } from 'src/trpc';

export const Menu: FC<{
  orderId: string;
  orderTotal: number;
  pricingOrder: Order;
  pricingCart: Cart;
  showTotalTable: boolean;
  discounts: DiscountType[];
  lineItems: AdditionalLineItem[];
  addOns: OrderAddOn[];
  orderStatus: OrderStatus;
  userId: string;
}> = memo(
  ({
    orderId,
    orderTotal,
    pricingOrder,
    pricingCart,
    discounts,
    lineItems,
    addOns,
    showTotalTable = true,
    orderStatus,
    userId,
  }) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [openPickupRequestDialog, setOpenPickupRequestDialog] = useState(false);
    const [openDiscountDialog, setOpenDiscountDialog] = useState(false);
    const [openLineItemDialog, setOpenLineItemDialog] = useState(false);
    const [openAddOnsDialog, setOpenAddOnsDialog] = useState(false);
    const [openCreatePaymentDialog, setOpenCreatePaymentDialog] = useState(false);
    const [openManualStatusChangeDialog, setOpenManualStatusChangeDialog] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const utils = trpc.useUtils();

    // Memoize the line items key to prevent unnecessary re-renders
    const lineItemsKey = useMemo(() => {
      return lineItems.map((li) => li.label).join('');
    }, [lineItems]);

    const handleModalClose = () => {
      setOpenDialog(false);
      setOpenPickupRequestDialog(false);
      setOpenDiscountDialog(false);
      setOpenLineItemDialog(false);
      setOpenAddOnsDialog(false);
      setOpenCreatePaymentDialog(false);
      setOpenManualStatusChangeDialog(false);
      setMenuOpen(false);
    };

    const { mutate } = trpc.admin.order.adminSetOrderStatus.useMutation({
      onSuccess: () => {
        utils.invalidate();
      },
    });

    const cancelOrder = () => {
      mutate({
        status: OrderStatusEnum.Cancelled,
        orderId: orderId,
      });
    };

    const CancelOrderDialog: FC = () => {
      return (
        <AlertDialog open={openDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-left text-text-light text-lg">
                Confirm Cancellation
              </AlertDialogTitle>
            </AlertDialogHeader>
            <p className="text-sm text-muted">Once you confirm, the customer will be notified.</p>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleModalClose}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  handleModalClose();
                  cancelOrder();
                }}
              >
                Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      );
    };

    return (
      <>
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" className="text-text-light">
              Actions
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 !max-h-none overflow-visible">
            <DropdownMenuLabel>Pricing Adjustments</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="py-4"
                onClick={() => {
                  setOpenDiscountDialog(true);
                  setMenuOpen(false);
                }}
              >
                Discounts
              </DropdownMenuItem>
              <DropdownMenuItem
                className="py-4"
                onClick={() => {
                  setOpenLineItemDialog(true);
                  setMenuOpen(false);
                }}
              >
                Custom Charges
              </DropdownMenuItem>
              <DropdownMenuItem
                className="py-4"
                onClick={() => {
                  setOpenAddOnsDialog(true);
                  setMenuOpen(false);
                }}
              >
                Add-ons & Products
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Payments</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <TooltipProvider>
                <TooltipRoot>
                  <TooltipTrigger asChild>
                    <div>
                      <DropdownMenuItem
                        className="py-4"
                        onClick={() => {
                          setOpenCreatePaymentDialog(true);
                          setMenuOpen(false);
                        }}
                      >
                        Record Payment
                      </DropdownMenuItem>
                    </div>
                  </TooltipTrigger>
                </TooltipRoot>
              </TooltipProvider>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            {orderStatus === 'drop-off-confirmed' && (
              <>
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    className="py-4"
                    onClick={() => {
                      setOpenPickupRequestDialog(true);
                      setMenuOpen(false);
                    }}
                  >
                    Request Pickup
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuLabel>Status Management</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="py-4"
                onClick={() => {
                  setOpenManualStatusChangeDialog(true);
                  setMenuOpen(false);
                }}
              >
                Manual Status Change
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="py-4"
                onClick={() => {
                  setOpenDialog(true);
                  setMenuOpen(false);
                }}
              >
                <span className="text-destructive font-medium">Cancel Order</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <CancelOrderDialog />
        <PickupRequestModal
          orderId={orderId}
          open={openPickupRequestDialog}
          onClose={handleModalClose}
        />
        {openDiscountDialog && (
          <DiscountDialog
            showTotalTable={showTotalTable}
            orderTotal={safeMoneyFromCents(orderTotal)}
            onClose={handleModalClose}
            open={openDiscountDialog}
            orderId={orderId}
            discounts={discounts}
            pricingContext={{ order: pricingOrder, cart: pricingCart }}
          />
        )}
        {openLineItemDialog && (
          <AdditionalLineItemDialog
            showTotalTable={showTotalTable}
            orderTotal={safeMoneyFromCents(orderTotal)}
            onClose={handleModalClose}
            open={openLineItemDialog}
            key={lineItemsKey}
            orderId={orderId}
            lineItems={lineItems}
          />
        )}
        {openCreatePaymentDialog && (
          <CreatePaymentDrawer
            open={openCreatePaymentDialog}
            onClose={handleModalClose}
            orderId={orderId}
            userId={userId}
          />
        )}
        {openManualStatusChangeDialog && (
          <ManualStatusChangeDialog
            open={openManualStatusChangeDialog}
            onClose={handleModalClose}
            orderId={orderId}
            currentStatus={orderStatus}
          />
        )}
        {openAddOnsDialog && (
          <OrderAddOnsDialog
            open={openAddOnsDialog}
            onClose={handleModalClose}
            orderId={orderId}
            addOns={addOns}
          />
        )}
      </>
    );
  },
);

Menu.displayName = 'Menu';
